/**
 * Pure series statistics for the Monitoring Center (`/monitor`).
 *
 * Operates on `{ ts, value }` points (epoch-ms timestamps) as returned
 * by the agent's `/api/timeseries` endpoint. No I/O here — callers fetch.
 */
/**
 * Compute min/max/avg/current/change for a window of points.
 * Returns `null` for an empty series. Non-finite values are ignored;
 * if none remain, returns `null`.
 */
export function computeSeriesStats(points) {
    const clean = points.filter((p) => Number.isFinite(p.value));
    if (clean.length === 0)
        return null;
    const sorted = [...clean].sort((a, b) => a.ts - b.ts);
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    let sum = 0;
    for (const p of sorted) {
        if (p.value < min)
            min = p.value;
        if (p.value > max)
            max = p.value;
        sum += p.value;
    }
    const first = sorted[0].value;
    const current = sorted[sorted.length - 1].value;
    const changeAbs = current - first;
    const changePct = first === 0 ? null : (changeAbs / Math.abs(first)) * 100;
    return {
        current,
        first,
        min,
        max,
        avg: sum / sorted.length,
        changeAbs,
        changePct,
    };
}
const MS_PER_DAY = 24 * 60 * 60 * 1_000;
/**
 * Least-squares linear-regression slope of the series in *units per day*.
 * Returns `null` when fewer than 2 finite points or zero time spread.
 * A negative result on alk/ca/mg windows reads as daily consumption.
 */
export function linearRegressionPerDay(points) {
    const clean = points.filter((p) => Number.isFinite(p.value));
    if (clean.length < 2)
        return null;
    const n = clean.length;
    // Work in days relative to the first timestamp for numeric stability.
    const t0 = clean[0].ts;
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumXX = 0;
    for (const p of clean) {
        const x = (p.ts - t0) / MS_PER_DAY;
        sumX += x;
        sumY += p.value;
        sumXY += x * p.value;
        sumXX += x * x;
    }
    const denom = n * sumXX - sumX * sumX;
    if (denom === 0)
        return null;
    return (n * sumXY - sumX * sumY) / denom;
}
/**
 * Bucket a slope (units/day) into a direction using a per-parameter
 * epsilon (defaults to a tiny absolute threshold).
 */
export function slopeDirection(slopePerDay, epsilon = 1e-6) {
    if (slopePerDay === null || Math.abs(slopePerDay) <= epsilon)
        return 'stable';
    return slopePerDay > 0 ? 'rising' : 'falling';
}
//# sourceMappingURL=stats.js.map